import {
  Component,
  Input,
  Output,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ScreenSharingService } from '../../services/screen-sharing.service';
import { RvshareAppDetectionService } from '../../services/rvshare-app-detection.service';
import { MatSelectChange } from '@angular/material/select';

const IDLE_CHECK_INTERVAL_MS = 1000;

@Component({
  selector: 'screen-sharing-controls',
  templateUrl: './screen-sharing-controls.component.html',
  styleUrl: './screen-sharing-controls.component.scss',
  standalone: false,
})
export class ScreenSharingControlsComponent implements OnInit, OnDestroy {
  @Input() isStopped: boolean;
  @Input() isPaused: boolean;
  @Input() isModerator?: boolean;
  @Input() moderatorName?: string;
  @Output() startStream: EventEmitter<void> = new EventEmitter<void>();
  @Output() pauseStream: EventEmitter<void> = new EventEmitter<void>();
  @Output() resumeStream: EventEmitter<void> = new EventEmitter<void>();
  @Output() stopStream: EventEmitter<void> = new EventEmitter<void>();
  @Output() endSession: EventEmitter<void> = new EventEmitter<void>();

  // Whole seconds, so the view only updates when the countdown it shows changes.
  readonly idleSecondsLeft = signal<number | null>(null);
  // The time left as m:ss once the warning shows; null before then.
  readonly idleCountdown = computed(() => {
    const seconds = this.idleSecondsLeft();
    if (seconds === null) {
      return null;
    }
    const minutesPart = Math.floor(seconds / 60);
    const secondsPart = String(seconds % 60).padStart(2, '0');
    return `${minutesPart}:${secondsPart}`;
  });

  private idleCheckInterval: ReturnType<typeof setInterval>;

  isElectron = window.electron?.isRVShareElectron === true;

  get waiting(): boolean {
    return (
      this.screenSharingService.isModerated &&
      !this.isModerator &&
      this.isStopped
    );
  }

  get canResumeSharing(): boolean {
    return this.isPaused && this.screenSharingService.memberIsSharing;
  }

  // A moderated session's volume and quality are its moderators'; a standard session's are its sharer's, in the RV
  // Share app too
  get showVolumeAndQuality(): boolean {
    return (
      !this.isStopped &&
      (!this.screenSharingService.isModerated ||
        !!this.screenSharingService.moderator)
    );
  }

  // For the member sharing without audio, and for the moderators
  get sharerAudioWarning(): string | null {
    const sharerAudio = this.screenSharingService.sharerAudio;
    if (this.isStopped || !sharerAudio || sharerAudio.status === 'shared') {
      return null;
    }
    const ownShare = this.screenSharingService.isOwnShare(sharerAudio.memberId);
    if (!ownShare && !this.screenSharingService.moderator) {
      return null;
    }
    if (sharerAudio.status === 'unavailable') {
      return 'Sharing without audio.';
    }
    if (ownShare) {
      return sharerAudio.status === 'unsupported'
        ? "Audio isn't supported on this device."
        : "You're sharing without audio.";
    }
    return sharerAudio.status === 'unsupported'
      ? "Audio isn't supported on the participant's device."
      : 'The participant is sharing without audio.';
  }

  // Nothing reaches the display: this member isn't sharing (or has paused), and nobody else is.
  private get sessionIdle(): boolean {
    if (this.screenSharingService.memberIsSharing) {
      return this.isStopped || this.isPaused;
    }
    return !this.screenSharingService.sharing;
  }

  private ngZone = inject(NgZone);

  constructor(
    protected screenSharingService: ScreenSharingService,
    protected rvshareAppDetectionService: RvshareAppDetectionService,
  ) {}

  ngOnInit(): void {
    // Outside the zone so the tick doesn't run change detection every second; the signal updates the view.
    this.ngZone.runOutsideAngular(() => {
      this.idleCheckInterval = setInterval(
        () => this.checkIdle(),
        IDLE_CHECK_INTERVAL_MS,
      );
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.idleCheckInterval);
  }

  // The server keeps the session's time, warns its streamer (or a moderated session's moderators), and ends it; this
  // only shows the time left. Hidden as soon as someone shares, before the server takes the warning down.
  checkIdle(): void {
    const endsAt = this.screenSharingService.idleSessionEndsAt;
    if (endsAt === null || !this.sessionIdle) {
      this.idleSecondsLeft.set(null);
      return;
    }
    this.idleSecondsLeft.set(
      Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)),
    );
  }

  stayInSession() {
    this.screenSharingService.reportSessionActivity();
  }

  start() {
    this.startStream.emit();
  }

  pause() {
    this.pauseStream.emit();
  }

  resume() {
    this.resumeStream.emit();
  }

  stop() {
    this.stopStream.emit();
  }

  end() {
    this.endSession.emit();
  }

  changeVolume(value: number) {
    this.screenSharingService.changeVolume(value);
  }

  onChangeQuality(event: MatSelectChange) {
    this.screenSharingService.changeQuality(event.value);
  }

  toggleInputCapture(enabled: boolean) {
    this.screenSharingService.sendInputCaptureToggle(enabled);
  }
}
